const healthProgressApiBase = () =>
    (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const ensureHealthProgressBackendToken = async () => {
    if (typeof window === "undefined") return "";

    const backendToken = (
        sessionStorage.getItem("backendToken") || ""
    ).trim();

    if (backendToken) {
        return backendToken;
    }

    const idToken = (
        sessionStorage.getItem("authToken") ||
        sessionStorage.getItem("bypassToken") ||
        ""
    ).trim();

    if (!idToken) {
        return "";
    }

    const response = await fetch(
        `${healthProgressApiBase()}/api/standalone/auth/verify`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                idToken,
            }),
        }
    );

    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result?.token) {
        const error = new Error(
            result?.message ||
                result?.error ||
                "Backend token verification failed. Please sign in again."
        );

        error.status = response.status;

        throw error;
    }

    sessionStorage.setItem("backendToken", result.token);

    return result.token;
};

const getHealthProgressAuthHeaders = async (headers = {}) => {
    const token = await ensureHealthProgressBackendToken();

    if (!token) {
        const error = new Error(
            "Authentication required. Please sign in again."
        );

        error.status = 401;

        throw error;
    }

    return {
        ...headers,
        Authorization: `Bearer ${token}`,
    };
};

const parseHealthProgressResponse = async (
    response,
    defaultMessage
) => {
    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
        const error = new Error(
            response.status === 409
                ? "This note is already finalized."
                : result?.message ||
                      result?.error ||
                      defaultMessage
        );

        error.status = response.status;
        error.code = result?.error;

        throw error;
    }

    if (
        result?.success === true &&
        Object.prototype.hasOwnProperty.call(result, "data")
    ) {
        return result.data;
    }

    return result;
};

export const fetchhealthProgressNotesByAppointment = async (
    apptId,
    userID
) => {
    const id = `${userID}_${apptId}_behavioral_health_note`;

    const response = await fetch(
        `${healthProgressApiBase()}/api/health-progress-notes/${encodeURIComponent(
            id
        )}?userID=${encodeURIComponent(userID)}`,
        {
            headers: await getHealthProgressAuthHeaders(),
        }
    );

    return parseHealthProgressResponse(
        response,
        "Failed to fetch Behavioral Health Progress Note"
    );
};

export const patchHealthProgressNotes = async (
    appId,
    userId,
    updatedNotes
) => {
    const id = `${userId}_${appId}_behavioral_health_note`;

    const response = await fetch(
        `${healthProgressApiBase()}/api/health-progress-notes/${encodeURIComponent(
            id
        )}?userID=${encodeURIComponent(userId)}`,
        {
            method: "PATCH",
            headers: await getHealthProgressAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                healthProgressNote: updatedNotes,
            }),
        }
    );

    return parseHealthProgressResponse(
        response,
        "Failed to save Behavioral Health Progress Note"
    );
};

export const finalizeHealthProgressNotes = async (
    appId,
    userId
) => {
    const id = `${userId}_${appId}_behavioral_health_note`;

    const response = await fetch(
        `${healthProgressApiBase()}/api/health-progress-notes/${encodeURIComponent(
            id
        )}/finalize?userID=${encodeURIComponent(userId)}`,
        {
            method: "PATCH",
            headers: await getHealthProgressAuthHeaders(),
        }
    );

    return parseHealthProgressResponse(
        response,
        "Failed to finalize Behavioral Health Progress Note"
    );
};